import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { WhiteLabelBlotterTrade } from '@approot/models/white-label-blotter';
import { AuthService } from '@approot/services';
import { WindowIndexService } from '@approot/widgets/rfq/rfq-container/window-index.service';
import {
  AgCartesianChartOptions,
  AgCharts,
  AgScatterSeriesOptions
} from 'ag-charts-community';
import { forkJoin, Observable } from 'rxjs';
import {
  LiquidityProviderEventsResponse,
  WhiteLabelClientEvent,
  WhiteLabelClientEventsResponse
} from '../white-label-blotter.types';
import {
  chartAxis,
  getColorForProvider,
  groupDataByProvider,
  pushToScatterGraph
} from './deal-details-graph-funcs';
import { ScatterDataPoint } from './deal-details-graph.types';

const WIN_POSITION_MIN_X = 8;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;

@Component({
  selector: 'app-deal-details-graph',
  templateUrl: './deal-details-graph.component.html',
  styleUrls: ['./deal-details-graph.component.scss']
})
export class DealDetailsGraphComponent implements OnInit {
  @Input() selectedTrade: WhiteLabelBlotterTrade;
  @Input() public scatterData: ScatterDataPoint[] = [];
  @Output() public closeComponent = new EventEmitter<void>();
  @ViewChild('scatterPlotContainer', { static: false })
  public scatterPlotContainer: ElementRef;
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  public dragging = false;
  public topPosition = 50;
  public leftPosition = 50;
  public windowTop = 39;
  public windowLeft = 950;
  public elementRef: ElementRef | null = null;
  public chartOptions: AgCartesianChartOptions | null = null;
  public showContainer = true;
  public assignedProviderColors: Record<string, string> = {};
  public scatterDataPoints: ScatterDataPoint[] = [];
  public status: 'Loading' | 'Loaded' | 'Error';
  private renderer = inject(Renderer2);
  private winIdx = inject(WindowIndexService);
  private authService = inject(AuthService);

  public ngOnInit(): void {
    this.initApiCalls();
  }

  public initApiCalls(): void {
    this.status = 'Loading';
    const apiCalls = {
      liquidityProviderEvents: this.getLiquidityProviderEventsByRequestId(
        this.selectedTrade.requestId
      ),
      whiteLabelClientEvents: this.getWhiteLabelClientEventsByRequestId(
        this.selectedTrade.requestId
      )
    };

    forkJoin(apiCalls).subscribe({
      next: calls => {
        let scatterData = [];
        if (calls.liquidityProviderEvents) {
          const liquidityEvents = calls.liquidityProviderEvents.response.items;
          const liquidityPoints = liquidityEvents
            .map((item: any) => this.handlePointQuotes(item))
            .flat();
          scatterData.push(...liquidityPoints);
        }

        if (calls.whiteLabelClientEvents) {
          const whiteLabelPoints = calls.whiteLabelClientEvents.response.items
            .map(item => this.handlePointQuotes(item, true))
            .flat();
          scatterData.push(...whiteLabelPoints);
        }
        this.status = 'Loaded';
        this.openDealDetails(scatterData);
      },
      error: _ => {
        this.status = 'Error';
      }
    });
  }

  public initializeChart(): void {
    const chartContainer = this.scatterPlotContainer?.nativeElement;
    if (!chartContainer) {
      return;
    }

    const groupedData = groupDataByProvider(this.scatterData);
    const series: AgScatterSeriesOptions[] = [];

    Object.keys(groupedData).forEach((provider, index) => {
      let size = 15;
      let color = getColorForProvider(
        provider,
        this.assignedProviderColors,
        index
      );
      const isOwnQuote = groupedData[provider].find(point => point.ownQuote);

      if (isOwnQuote) {
        color = '#f5f0e4';
        size = 20;
      }
      pushToScatterGraph(series, color, size, provider, groupedData);
    });

    AgCharts.create(chartAxis(chartContainer, series));
  }

  public updateChartData(data: ScatterDataPoint[]): void {
    if (data.length === 0) {
      console.warn('No data to render the scatter plot.');
      return;
    }

    this.scatterData = data;
    this.initializeChart();
  }

  public openContainer(): void {
    this.showContainer = true;
    this.updateZIndex();
  }

  public closeContainer(): void {
    this.showContainer = false;
    this.closeComponent.emit();
  }

  public dragStart(): void {
    this.updateZIndex();
  }

  public async dragEnd(): Promise<void> {
    this.dragging = false;
    const el = this.scatterPlotContainer.nativeElement.querySelector(
      '.deal-details-container-drag'
    );

    if (!el) {
      console.error('Draggable element not found.');
      return;
    }

    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - rect.x;
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public updateZIndex(): void {
    if (!this.scatterPlotContainer?.nativeElement) {
      console.error('scatterPlotContainer is not defined or not rendered yet.');
      return;
    }

    const draggableElement = this.scatterPlotContainer.nativeElement.closest(
      '.deal-details-container-drag'
    );

    if (draggableElement) {
      this.winIdx.setWindowIndex(draggableElement, this.renderer);
    } else {
      console.error('Draggable element not found within scatterPlotContainer.');
    }
  }

  private getLiquidityProviderEventsByRequestId(
    requestId: number
  ): Observable<LiquidityProviderEventsResponse> {
    const payload = {
      task: 'getLiquidityProviderEventsByRequestId',
      requestId
    };
    return this.authService.fxPost(
      payload,
      `whiteLabelServices`,
      `task=${payload.task}`
    );
  }

  private getWhiteLabelClientEventsByRequestId(
    requestId: number
  ): Observable<WhiteLabelClientEventsResponse> {
    const payload = {
      task: 'getWhiteLabelClientEventsByRequestId',
      requestId
    };
    return this.authService.fxPost(
      payload,
      `whiteLabelServices`,
      `task=${payload.task}`
    );
  }

  private handlePointQuotes(
    item: WhiteLabelClientEvent,
    ownQuote = false
  ): ScatterDataPoint[] {
    if (
      item.event['@type'] === 'ClientQuoteEvent' &&
      item.event.action === 'ConfirmQuoteSubmit'
    ) {
      const timestamp = item.timestamp;
      if (item.event.quote) {
        return this.quotePoint(item.event.quote, timestamp, ownQuote);
      } else if (item.event.quotes) {
        return item.event.quotes.flatMap(quote =>
          this.quotePoint(quote, timestamp, ownQuote)
        );
      }
    }
    return [];
  }

  public openDealDetails(data: ScatterDataPoint[]): void {
    this.scatterDataPoints = data.map(item => ({
      timestamp: item.timestamp,
      premium: item.premium,
      type: item.type,
      provider: item.provider,
      ownQuote: item.ownQuote
    }));
    setTimeout(_ => {
      this.updateChartData(this.scatterDataPoints);
    }, 0);
  }

  private quotePoint(
    quote: any,
    timestamp: number,
    ownQuote = false
  ): ScatterDataPoint[] {
    const premiumBid = quote.premiumAmountBid;
    const premiumOffer = quote.premiumAmountOffer;
    const provider = quote.provider || quote.providerShortName;

    return [
      {
        timestamp: timestamp,
        premium: premiumBid,
        type: 'Bid',
        provider: provider,
        ownQuote
      },
      {
        timestamp: timestamp,
        premium: premiumOffer,
        type: 'Offer',
        provider: provider,
        ownQuote
      }
    ].filter(quote => quote.premium) as ScatterDataPoint[];
  }
}
